import { useState } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Button,
  Label,
} from '@pantry/ui';
import { useUpdateMemberRole, type MemberWithUser, type InvitationRole } from '@pantry/api-client';
import { ShieldCheckIcon } from '@heroicons/react/24/outline';

interface ChangeMemberRoleDialogProps {
  householdId: string;
  member: MemberWithUser;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ChangeMemberRoleDialog({
  householdId,
  member,
  open,
  onOpenChange,
}: ChangeMemberRoleDialogProps) {
  const { t } = useLingui();
  const [role, setRole] = useState<InvitationRole>(member.role as InvitationRole);
  const [error, setError] = useState<string | null>(null);

  const updateRole = useUpdateMemberRole(householdId);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (role === member.role) {
      onOpenChange(false);
      return;
    }

    try {
      await updateRole.mutateAsync({ userId: member.userId, role });
      onOpenChange(false);
    } catch (err) {
      const message = err instanceof Error ? err.message : t`Failed to update role`;
      setError(message);
    }
  };

  const memberName = member.user.name || member.user.email;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10">
              <ShieldCheckIcon className="h-5 w-5 text-primary" />
            </div>
            <Trans>Change Role</Trans>
          </DialogTitle>
          <DialogDescription className="pt-2">
            <Trans>
              Update the role for <span className="font-medium text-foreground">{memberName}</span>.
            </Trans>
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="member-role">
              <Trans>Role</Trans>
            </Label>
            <select
              id="member-role"
              value={role}
              onChange={(e) => setRole(e.target.value as InvitationRole)}
              className="flex h-10 w-full rounded-xl border border-border/50 bg-muted/30 px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 focus-visible:border-primary/50 transition-all cursor-pointer hover:border-border"
            >
              <option value="member">{t`Member`}</option>
              <option value="admin">{t`Admin`}</option>
            </select>
          </div>

          <div className="rounded-xl bg-muted/50 border border-border/50 p-4 text-sm space-y-2">
            <p className="font-medium text-foreground">
              <Trans>Role Permissions:</Trans>
            </p>
            <ul className="text-muted-foreground space-y-1.5 text-xs">
              <li className="flex gap-2">
                <span className="font-medium text-foreground">
                  <Trans>Member:</Trans>
                </span>{' '}
                <Trans>View and edit items, storage locations</Trans>
              </li>
              <li className="flex gap-2">
                <span className="font-medium text-foreground">
                  <Trans>Admin:</Trans>
                </span>{' '}
                <Trans>Everything members can do, plus manage members and household settings</Trans>
              </li>
            </ul>
          </div>

          {error && (
            <div className="rounded-xl bg-destructive/10 border border-destructive/20 p-3 text-sm text-destructive animate-in fade-in slide-in-from-top-2">
              {error}
            </div>
          )}

          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={updateRole.isPending}
              className="rounded-xl"
            >
              <Trans>Cancel</Trans>
            </Button>
            <Button
              type="submit"
              disabled={updateRole.isPending || role === member.role}
              className="rounded-xl"
            >
              {updateRole.isPending ? (
                <div className="flex items-center gap-2">
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                  <Trans>Saving...</Trans>
                </div>
              ) : (
                <Trans>Save Changes</Trans>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
